import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { selectScanDataTableExists } from '../../duckDB/duckDBSlice';
import { DuckQueryReturnListType } from '../../duckDB/hooks/useDuckQueryScanData';
import { ScanDataEnum } from '../scanDataDefinitions';
import { useGetIntervalAndMeasure } from './useGetIntervalAndMeasure';
import { useScanDataQuery } from './useScanDataQuery';

export enum StdDevFilterEnum {
  class_name = ScanDataEnum.class_name,
  model_name = ScanDataEnum.model_name,
  model_version = ScanDataEnum.model_version,
}

export interface IntervalValue {
  interval_value: number;
}

export interface DistributionQueryResult extends IntervalValue {
  interval: number;
}

const group = ['interval'];
const order: [string, 'asc' | 'desc'][] = [['interval', 'asc']];

export const useScanDataDistribution = ({
  mean,
  skip = false,
}: {
  mean: number | null;
  skip?: boolean;
}): DuckQueryReturnListType<DistributionQueryResult> => {
  const { interval, measure } = useGetIntervalAndMeasure();
  const scanDataTableExists = useAppSelector(selectScanDataTableExists);

  const cols = useMemo(
    () => [
      /*
       * if average value is lesser than 1 - make interval separated by 1/100 values like 0.01, 0.02, etc.
       * if average value is lesser than 10 - make interval separated by 1/10 values like 0.1, 0.2, etc.
       * if average value is lesser than 100 - make interval separated by rounded values like 1, 2, 3, etc.
       * if average value is greater or equals 100 - make interval separated by each 10 values, like 10, 20, 30, etc.
       */
      `(CASE
    WHEN ${mean || 0} < 1 THEN FLOOR(${interval} * 100) / 100
    WHEN ${mean || 0} < 10 THEN FLOOR(${interval} * 10) / 10
    WHEN ${mean || 0} < 100 THEN FLOOR(${interval})
    ELSE FLOOR(${interval} / 10) * 10
    END) as interval`,

      `COUNT(${measure}) as interval_value`,
    ],
    [mean, interval, measure]
  );

  const scanDistributionFilteredByFeature =
    useScanDataQuery<DistributionQueryResult>({
      cols,
      group,
      order,
      useUnitsTransformSubstringMatching: true,
      skip: skip || !interval || !measure || !scanDataTableExists || !mean,
    });

  return scanDistributionFilteredByFeature;
};
